import {
  CandidateInviteStatus,
  CandidateProfileTableColumns
} from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import { CandidateSearchProfile } from 'modules/candidate-profile/types/CandidateSearchResult';
import { formatTimeInRole } from 'modules/candidate-profile/tables/maps/formatTimeInRole';
import { formatYearsOfExperience } from 'modules/candidate-profile/tables/maps/formatYearsOfExperience';

export type SearchVectorCandidateProfileQueryItem = CandidateSearchProfile;

type DuplicateCheckResult = {
  candidateProfileId: string;
  hasSourcedInvite: boolean;
  hasAnalysedInvite: boolean;
  hasDuplicate: boolean;
};

type MapCandidateProfileDataToTableColumnsProps = {
  data: SearchVectorCandidateProfileQueryItem[];
  duplicateCandidateProfileResults: DuplicateCheckResult[];
  targetTalentPoolId?: string | null;
};

const getInviteStatus = (
  candidateProfileListIds: string[],
  targetTalentPoolId: string | null | undefined,
  duplicateCandidateProfileResult: DuplicateCheckResult | undefined
): CandidateProfileTableColumns['inviteStatus'] => {
  // Check if candidate is already in the target talent pool
  if (
    targetTalentPoolId &&
    candidateProfileListIds.includes(targetTalentPoolId)
  ) {
    return CandidateInviteStatus.IN_LIST;
  }

  // Fall back to existing duplicate check logic
  if (duplicateCandidateProfileResult?.hasAnalysedInvite) {
    return CandidateInviteStatus.APPLIED;
  }
  if (duplicateCandidateProfileResult?.hasSourcedInvite) {
    return CandidateInviteStatus.INVITED;
  }

  return null;
};

const customFieldValues = (
  item: SearchVectorCandidateProfileQueryItem
): Record<string, unknown> => {
  const values = (item as { customFields?: unknown }).customFields;

  return values && typeof values === 'object' && !Array.isArray(values)
    ? (values as Record<string, unknown>)
    : {};
};

export const mapCandidateProfileDataToTableColumns = ({
  data,
  duplicateCandidateProfileResults,
  targetTalentPoolId
}: MapCandidateProfileDataToTableColumnsProps): CandidateProfileTableColumns[] => {
  const duplicateResultsMap = new Map(
    duplicateCandidateProfileResults.map((result) => [
      result.candidateProfileId,
      result
    ])
  );

  return data.map((item) => {
    const duplicateCandidateProfileResult = duplicateResultsMap.get(
      item.candidate_profile_id
    );
    const candidateProfileListIds = (
      item.candidate_profile_list_ids ?? []
    ).filter((id): id is string => id !== null);

    return {
      ...customFieldValues(item),
      id: item.candidate_profile_id,
      name: item.full_name ?? '',
      location: item.location_name ?? '',
      currentCompany: item.current_company ?? '',
      currentJobTitle: item.current_job_title ?? '',
      timeInRole: formatTimeInRole(item.current_role_start_date),
      yearsOfExperience: formatYearsOfExperience(item.years_of_experience),
      skills: item.skills ?? [],
      qualifications: item.qualifications ?? [],
      email: item.email ?? '',
      phone: item.phone ?? '',
      atsCandidateId: item.ats_candidate_id ?? '',
      externalUrl: item.external_url ?? '',
      originalSource: item.original_source ?? '',
      tags: item.tags ?? [],
      candidateProfileLists: candidateProfileListIds,
      score: item.score ?? null,
      explanation: item.explanation ?? null,
      inviteStatus: getInviteStatus(
        candidateProfileListIds,
        targetTalentPoolId,
        duplicateCandidateProfileResult
      )
    };
  });
};
